import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import styles from './VNCategorySearch.module.scss'
import { ButtonBase } from '@material-ui/core'
import { ReactComponent as CloseIcon } from '../../assets/icons/Close-Icon.svg'
import { ReactComponent as InputSearchIcon } from '../../assets/input-search-icon.svg'
import { getSelectedCategoryName } from '../../selectors/categories'
import { getCategorySearch } from '../Selectors'
import { setCategorySearch } from '../ActionCreators'
import { isE700Device } from '../../VNAndroidSDK/Utils'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_TIME = 175

/**
 * VNCategorySearch component allows users to search for items within current category
 *
 * @returns {JSX.Element}
 */
const VNCategorySearch = ({ hideSearch = () => {} }) => {
  const dispatch = useDispatch()
  const debounceTimer = useRef(null)
  const wrapperRef = useRef(null)
  const quickSearchRef = useRef(null)
  const [value, setValue] = useState('')
  const curCategory = useSelector(getSelectedCategoryName)
  const isMobile = useMemo(() => window.matchMedia('(max-width: 768px)').matches, [])
  const shouldDisplayClose = useMemo(() => !isMobile || value.length > 0, [isMobile, value])
  const query = useSelector(getCategorySearch)

  // close search and reset input
  const closeSearch = useCallback(() => {
    if (query.length) {
      dispatch(setCategorySearch(''))
    }
    setValue('')
    hideSearch()

    if (isMobile) {
      quickSearchRef.current.blur()
      return
    }
  }, [dispatch, hideSearch, isMobile, query.length])

  // clear input and hide search
  const clearInput = useCallback(
    (cancelSearch = true) => {
      setValue('')

      if (cancelSearch) {
        quickSearchRef.current.blur()
        hideSearch()
      }
    },
    [hideSearch],
  )

  useEffect(() => {
    // handle tapping outside the input to close search
    const handleTouchOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        quickSearchRef.current && quickSearchRef.current.blur()
      }
    }
    document.addEventListener('mousedown', handleTouchOutside)
    document.addEventListener('touchstart', handleTouchOutside)

    // cleanup functions
    return () => {
      document.removeEventListener('mousedown', handleTouchOutside)
      document.removeEventListener('touchstart', handleTouchOutside)
      clearTimeout(debounceTimer.current)
    }
  }, [])

  // handle input changes and debounce search
  useEffect(() => {
    if (query === value) return
    if (debounceTimer?.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      // has minimum search length
      if (value.length >= MIN_QUERY_LENGTH) {
        dispatch(setCategorySearch(value))
        return
      }

      // reset when previous searched and has less than minimum search length
      if (query.length) {
        dispatch(setCategorySearch(''))
      }
    }, DEBOUNCE_TIME)

    return () => clearTimeout(debounceTimer?.current)
  }, [value, query, dispatch, clearInput])

  // handle input changes
  const handleChange = (e) => {
    setValue(e.target.value)
  }
  const handleBlur = () => {
    if (query.length) return
    clearInput(true)
  }

  // on enter, blur the input to hide the keyboard
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return

    quickSearchRef.current && quickSearchRef.current.blur()
  }

  // search input properties
  const inputProps = {
    name: 'category-search-input',
    className: styles.input,
    type: 'text',
    value: value,
    onBlur: handleBlur,
    autoComplete: 'off',
    ref: quickSearchRef,
    autoFocus: !isMobile,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
  }

  // mobile search
  if (isMobile) {
    return (
      <div className={styles.wrapper} ref={wrapperRef}>
        <div className={styles.container}>
          <div className={styles.magnifier}>
            <InputSearchIcon />
          </div>
          <input {...inputProps} />
          {!!value.length && (
            <ButtonBase disableRipple={isE700Device()} element="div" className={styles.close} onClick={closeSearch}>
              <CloseIcon />
            </ButtonBase>
          )}
        </div>
      </div>
    )
  }

  // tablet and desktop search
  return (
    <div className={cn(styles.wrapper, styles.animate)} ref={wrapperRef}>
      <div className={styles.container}>
        <div className={styles.magnifier}>
          <InputSearchIcon />
        </div>
        {!value.length && (
          <div className={styles.placeholder}>
            Search for items in <b>{curCategory}</b>
          </div>
        )}
        <input {...inputProps} />
        {!!value.length && (
          <div className={styles.indicator}>
            in <b>{curCategory}</b>
          </div>
        )}
        {shouldDisplayClose && (
          <ButtonBase disableRipple={isE700Device()} element="div" className={styles.close} onClick={closeSearch}>
            <CloseIcon />
          </ButtonBase>
        )}
      </div>
    </div>
  )
}

export default VNCategorySearch
